import { includes, map } from "lodash";
import React, { useState, useEffect } from "react";
import Button from "antd/lib/button";
import { useTheme } from "@/components/ThemeProvider";

import routeWithUserSession from "@/components/application-area/routeWithUserSession";
import navigateTo from "@/components/application-area/navigateTo";
import Paginator from "@/components/items-list/components/Paginator";

import { wrap as itemsList } from "@/components/items-list/ItemsList";
import { ResourceItemsSource } from "@/components/items-list/classes/ItemsSource";
import { StateStorage } from "@/components/items-list/classes/StateStorage";

import LoadingState from "@/components/items-list/components/LoadingState";
import ItemsTable, { Columns } from "@/components/items-list/components/ItemsTable";
import SelectItemsDialog from "@/components/select-items-dialog/SelectItemsDialog";
import { UserPreviewCard } from "@/components/PreviewCard";

import GroupName from "@/components/groups/GroupName";
import ListItemAddon from "@/components/groups/ListItemAddon";
import Sidebar from "@/components/groups/DetailsPageSidebar";
import Layout from "@/components/layouts/ContentWithSidebar";
import wrapSettingsTab from "@/components/settings/SettingsWrapper";

import notification from "@/services/notification";
import { currentUser } from "@/services/auth";
import Group from "@/services/group";
import User from "@/services/user";
import routes from "@/services/routes";

import "./Groups.less";

function GroupMembers({ controller }) {
  const { isDarkMode } = useTheme();
  const [group, setGroup] = useState(null);
  const groupId = parseInt(controller.params.groupId, 10);

  useEffect(() => {
    Group.get({ id: groupId })
      .then(fetchedGroup => {
        setGroup(fetchedGroup);
      })
      .catch(error => {
        controller.handleError(error);
      });
  }, [groupId, controller]);

  const sidebarMenu = [
    {
      key: "users",
      href: `groups/${groupId}`,
      title: "Members",
    },
    {
      key: "datasources",
      href: `groups/${groupId}/data_sources`,
      title: "Data Sources",
      isAvailable: () => currentUser.isAdmin,
    },
  ];

  const listColumns = [
    Columns.custom((text, user) => <UserPreviewCard user={user} withLink />, {
      title: "Name",
      field: "name",
      width: null,
    }),
    Columns.custom(
      (text, user) => {
        if (!group) {
          return null;
        }

        // cannot remove self from built-in groups
        if (group.type === "builtin" && currentUser.id === user.id) {
          return null;
        }
        return (
          <Button className="w-100" type="danger" onClick={event => removeGroupMember(event, user)}>
            Remove
          </Button>
        );
      },
      {
        width: "1%",
        isAvailable: () => currentUser.isAdmin,
      }
    ),
  ];

  const removeGroupMember = (event, user) =>
    Group.removeMember({ id: groupId, userId: user.id })
      .then(() => {
        controller.updatePagination({ page: 1 });
        controller.update();
      })
      .catch(() => {
        notification.error("Failed to remove member from group.");
      });

  const addMembers = () => {
    const alreadyAddedUsers = map(controller.allItems, u => u.id);
    SelectItemsDialog.showModal({
      dialogTitle: "Add Members",
      inputPlaceholder: "Search users...",
      selectedItemsTitle: "New Members",
      searchItems: searchTerm => User.query({ q: searchTerm }).then(({ results }) => results),
      renderItem: (item, { isSelected }) => {
        const alreadyInGroup = includes(alreadyAddedUsers, item.id);
        return {
          content: (
            <UserPreviewCard user={item}>
              <ListItemAddon isSelected={isSelected} alreadyInGroup={alreadyInGroup} />
            </UserPreviewCard>
          ),
          isDisabled: alreadyInGroup,
          className: isSelected || alreadyInGroup ? "selected" : "",
        };
      },
      renderStagedItem: (item, { isSelected }) => ({
        content: (
          <UserPreviewCard user={item}>
            <ListItemAddon isSelected={isSelected} isStaged />
          </UserPreviewCard>
        ),
      }),
    }).onClose(items => {
      const promises = map(items, u => Group.addMember({ id: groupId }, { user_id: u.id }));
      return Promise.all(promises).then(() => controller.update());
    });
  };

  return (
    <div className={`groups-page ${isDarkMode ? 'dark-mode' : ''}`} data-test="Group">
      <GroupName className="d-block m-t-0 m-b-15" group={group} onChange={() => setGroup({ ...group })} />
      <Layout>
        <Layout.Sidebar>
          <Sidebar
            controller={controller}
            group={group}
            items={sidebarMenu}
            canAddMembers={currentUser.isAdmin}
            onAddMembersClick={addMembers}
            onGroupDeleted={() => navigateTo("groups")}
          />
        </Layout.Sidebar>
        <Layout.Content>
          {!controller.isLoaded && <LoadingState className="" />}
          {controller.isLoaded && controller.isEmpty && (
            <div className="text-center">
              <p>There are no members in this group yet.</p>
              {currentUser.isAdmin && (
                <Button type="primary" onClick={addMembers}>
                  <i className="fa fa-plus m-r-5" aria-hidden="true" />
                  Add Members
                </Button>
              )}
            </div>
          )}
          {controller.isLoaded && !controller.isEmpty && (
            <div className="table-responsive">
              <ItemsTable
                items={controller.pageItems}
                columns={listColumns}
                showHeader={false}
                orderByField={controller.orderByField}
                orderByReverse={controller.orderByReverse}
                toggleSorting={controller.toggleSorting}
              />
              <Paginator
                showPageSizeSelect
                totalCount={controller.totalItemsCount}
                pageSize={controller.itemsPerPage}
                onPageSizeChange={itemsPerPage => controller.updatePagination({ itemsPerPage })}
                page={controller.page}
                onChange={page => controller.updatePagination({ page })}
              />
            </div>
          )}
        </Layout.Content>
      </Layout>
    </div>
  );
}

const GroupMembersPage = wrapSettingsTab(
  "Groups.Members",
  null,
  itemsList(
    GroupMembers,
    () =>
      new ResourceItemsSource({
        isPlainList: true,
        getRequest(unused, { params: { groupId } }) {
          return { id: groupId };
        },
        getResource() {
          return Group.members.bind(Group);
        },
      }),
    () => new StateStorage({ orderByField: "name" })
  )
);

routes.register(
  "Groups.Members",
  routeWithUserSession({
    path: "/groups/:groupId",
    title: "Group Members",
    render: pageProps => <GroupMembersPage {...pageProps} currentPage="users" />,
  })
);
